import { createContext, useContext, useState, type ReactNode } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useGlobalLoader } from "@/components/GlobalLoader";
import type { ContaConfigResponse } from "@/types/conta-config-response";
import inovaone_api from "@/services/inovaone_api";

export type ContaConfigContextType = {
  contaConfig: ContaConfigResponse | null;
  atualizarNumeroDeImpressoes: () => Promise<void>;
  numeroDeImpressoes: number;
  getContaConfig: () => Promise<ContaConfigResponse>;
};

export const ContaConfigContext = createContext<
  ContaConfigContextType | undefined
>(undefined);

export function ContaConfigProvider({ children }: { children: ReactNode }) {
  const { setIsLoading } = useGlobalLoader();
  const { usuario } = useAuth();
  const [contaConfig, setContaConfig] = useState<ContaConfigResponse | null>(
    null,
  );
  const [numeroDeImpressoes, setNumeroDeImpressoes] = useState<number>(0);

  const getContaConfig = async (): Promise<ContaConfigResponse> => {
    try {
      const conta_config_response = await inovaone_api.get<
        ContaConfigResponse[]
      >(`/conta_config/${usuario?.id_conta}`);
      const contaConfigData = conta_config_response.data[0];
      setContaConfig(contaConfigData);
      setNumeroDeImpressoes(contaConfigData?.numero_de_impressoes || 0);
      return contaConfigData;
    } catch (error) {
      console.error("Erro ao buscar configuração da conta:", error);
      throw error;
    }
  };

  const atualizarNumeroDeImpressoes = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const numero_atual = contaConfig?.numero_de_impressoes || 0;
      const novo_numero = numero_atual + 1;
      const response_conta_config = await inovaone_api.post<
        ContaConfigResponse[]
      >(`/conta_config`, [
        {
          id_conta: contaConfig?.id_conta,
          numero_de_impressoes: novo_numero,
          _id: contaConfig?._id,
        },
      ]);
      setNumeroDeImpressoes(novo_numero);
      setContaConfig(response_conta_config.data[0]);
    } catch (error) {
      console.error("Erro ao atualizar número de impressões:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ContaConfigContext.Provider
      value={{
        contaConfig,
        atualizarNumeroDeImpressoes,
        numeroDeImpressoes,
        getContaConfig,
      }}
    >
      {children}
    </ContaConfigContext.Provider>
  );
}

export function useContaConfig() {
  const context = useContext(ContaConfigContext);
  if (!context) {
    throw new Error(
      "useContaConfig deve ser usado dentro de um ContaConfigProvider",
    );
  }
  return context;
}
